import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import {
  LanguageSwitcher,
  logout,
  selectActiveOrganization,
  selectCanManageOrganization,
  selectCurrentUser,
  selectIsGuest,
  selectIsImpersonating,
  selectIsStaff,
  selectIsSuperuser,
  useListAllOrganizationsQuery,
  useLogoutMutation,
  useSwitchOrganization,
} from '@features/auth';
import { selectCurrentProject } from '@features/projects';

import { startWorkspaceTour } from '../store/workspaceSlice';
import { CreateOrganizationModal } from './CreateOrganizationModal';
import { ImpersonateTenantModal } from './ImpersonateTenantModal';

/**
 * Account menu: organization switching, tenant tools, settings and sign out.
 *
 * Lives in the workspace header rather than the sidebar because the sidebar
 * disappears in the exact situations where switching organization matters
 * most: an organization with no projects yet, and a collapsed sidebar.
 */
export function AccountMenu() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const currentProject = useAppSelector(selectCurrentProject);
  const isGuest = useAppSelector(selectIsGuest);
  const canManageOrganization = useAppSelector(selectCanManageOrganization);
  const isStaff = useAppSelector(selectIsStaff);
  const isSuperuser = useAppSelector(selectIsSuperuser);
  const isImpersonating = useAppSelector(selectIsImpersonating);
  const organizations = user?.organizations ?? [];
  const { switchTo, createAndSwitch, impersonate, isSwitching, isCreating, isStartingImpersonation } = useSwitchOrganization();
  const [endSession] = useLogoutMutation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showCreateOrganization, setShowCreateOrganization] = useState(false);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // Superuser-only endpoint: only fetched once the picker is opened.
  const { data: allOrganizations = [], isLoading: isLoadingAllOrganizations } = useListAllOrganizationsQuery(undefined, {
    skip: !showImpersonate,
  });
  const userMenuRef = useRef(null);
  const userMenuButtonRef = useRef(null);

  // While impersonating, the only sane organization action is the banner's
  // Stop — hide switching, creation and the picker.
  const showsOrganization = !isImpersonating && (organizations.length > 1 || isStaff || isSuperuser);

  useEffect(() => {
    if (!showUserMenu) return undefined;
    const closeMenu = (event) => {
      if (event.key === 'Escape' || (event.type === 'mousedown' && !userMenuRef.current?.contains(event.target))) {
        setShowUserMenu(false);
        if (event.key === 'Escape') userMenuButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', closeMenu);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', closeMenu);
    };
  }, [showUserMenu]);

  const handleLogout = async () => {
    try {
      await endSession().unwrap();
    } finally {
      dispatch(logout());
      window.location.href = '/login';
    }
  };

  return (
    <>
      <div ref={userMenuRef} className="relative shrink-0">
        <button
          type="button"
          ref={userMenuButtonRef}
          onClick={() => setShowUserMenu(!showUserMenu)}
          title={t('workspace.accountMenu.title')}
          aria-label={t('workspace.accountMenu.open')}
          aria-expanded={showUserMenu}
          className="flex cursor-pointer items-center gap-2 rounded-lg py-1 pe-1.5 ps-1 transition-colors hover:bg-[var(--color-bg-muted)]"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-subtle)] text-xs font-semibold text-[var(--color-accent)] ring-1 ring-inset ring-[var(--color-accent)]/15">
            {user?.email?.charAt(0).toUpperCase() || '?'}
          </span>
          {showsOrganization && activeOrganization && (
            <span
              data-testid="active-organization"
              className="hidden max-w-36 truncate text-xs font-semibold text-[var(--color-text)] sm:block"
            >
              {activeOrganization.name}
            </span>
          )}
          <Icon icon="mdi:chevron-down" className="size-4 shrink-0 text-[var(--color-text-muted)]" />
        </button>
        {showUserMenu && (
          <div className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-[var(--color-border)] bg-white p-1.5 shadow-xl">
            <div className="px-3 pb-2 pt-1.5">
              <p className="truncate text-xs font-semibold text-[var(--color-text)]">{user?.first_name || t('workspace.accountMenu.accountFallback')}</p>
              <p className="truncate text-[10px] text-[var(--color-text-muted)]">{user?.email}</p>
            </div>
            <LanguageSwitcher className="px-3 pb-2" />
            <div className="my-1 border-t border-[var(--color-border)]" />
            {showsOrganization && (
              <>
                <p className="px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">{t('workspace.accountMenu.organization')}</p>
                {organizations.length > 1 && organizations.map((organization) => (
                  <button
                    key={organization.uuid}
                    type="button"
                    role="menuitemradio"
                    aria-checked={organization.uuid === activeOrganization?.uuid}
                    disabled={isSwitching}
                    onClick={() => {
                      if (organization.uuid === activeOrganization?.uuid) {
                        setShowUserMenu(false);
                        return;
                      }
                      setShowUserMenu(false);
                      switchTo(organization.uuid);
                    }}
                    className="cursor-pointer flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-muted)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon
                      icon={organization.uuid === activeOrganization?.uuid ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'}
                      className={`size-4 ${organization.uuid === activeOrganization?.uuid ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-muted)]'}`}
                    />
                    <span className="truncate">{organization.name}</span>
                  </button>
                ))}
                {isStaff && (
                  <button
                    type="button"
                    onClick={() => { setShowUserMenu(false); setShowCreateOrganization(true); }}
                    className="cursor-pointer flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]"
                  >
                    <Icon icon="mdi:domain-plus" className="size-4 text-[var(--color-text-muted)]" /> {t('workspace.accountMenu.newOrganization')}
                  </button>
                )}
                {isSuperuser && (
                  <button
                    type="button"
                    onClick={() => { setShowUserMenu(false); setShowImpersonate(true); }}
                    className="cursor-pointer flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]"
                  >
                    <Icon icon="mdi:incognito" className="size-4 text-[var(--color-text-muted)]" /> {t('workspace.accountMenu.viewTenantAsGuest')}
                  </button>
                )}
                <div className="my-1 border-t border-[var(--color-border)]" />
              </>
            )}
            {(isSuperuser || canManageOrganization) && <Link to="/usage" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]">
              <Icon icon="mdi:chart-line" className="size-4" />
              {t('workspace.accountMenu.usage')}
            </Link>}
            {/* Guests have no settings; a superuser viewing a tenant is a guest by
                construction but must still reach every tenant's Settings. */}
            {(!isGuest || isSuperuser) && <Link to="/settings/knowledge-base" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]">
              <Icon icon="mdi:cog-outline" className="size-4 text-[var(--color-text-muted)]" /> {t('workspace.accountMenu.settings')}
            </Link>}
            {currentProject && <button type="button" onClick={() => { dispatch(startWorkspaceTour()); setShowUserMenu(false); }} className="cursor-pointer flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]">
              <Icon icon="mdi:help-circle-outline" className="size-4 text-[var(--color-text-muted)]" /> {t('workspace.accountMenu.productTour')}
            </button>}
            <div className="my-1 border-t border-[var(--color-border)]" />
            <button type="button" onClick={() => { setShowUserMenu(false); setShowLogoutConfirm(true); }} className="cursor-pointer flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-[var(--color-danger)] hover:bg-red-50">
              <Icon icon="mdi:logout" className="size-4" /> {t('workspace.accountMenu.signOut')}
            </button>
          </div>
        )}
      </div>

      {/* Rendered outside userMenuRef so the outside-click handler that
          closes the user menu cannot swallow clicks inside the modal. */}
      <CreateOrganizationModal
        isOpen={showCreateOrganization}
        onClose={() => setShowCreateOrganization(false)}
        onCreate={createAndSwitch}
        isSubmitting={isCreating}
      />

      <ImpersonateTenantModal
        isOpen={showImpersonate}
        onClose={() => setShowImpersonate(false)}
        onImpersonate={impersonate}
        organizations={allOrganizations}
        isLoading={isLoadingAllOrganizations}
        isSubmitting={isStartingImpersonation}
      />

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <div className="size-full fixed top-0 start-0 z-80 overflow-x-hidden overflow-y-auto flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowLogoutConfirm(false)} aria-hidden="true" />
          <div className="relative sm:max-w-md sm:w-full m-3 sm:mx-auto">
            <div className="flex flex-col bg-white border border-[var(--color-border)] shadow-sm rounded-lg">
              <div className="p-4 sm:p-7 text-center">
                <div className="flex justify-center mb-4">
                  <span className="inline-flex justify-center items-center size-12 rounded-full bg-amber-50 text-amber-600">
                    <Icon icon="mdi:logout" className="size-6" />
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-[var(--color-text)]">{t('workspace.accountMenu.signOut')}</h3>
                <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{t('workspace.accountMenu.signOutConfirm')}</p>
              </div>
              <div className="flex justify-end items-center gap-x-2 py-3 px-4 border-t border-[var(--color-border)]">
                <button
                  type="button"
                  onClick={() => setShowLogoutConfirm(false)}
                  className="cursor-pointer py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text)] shadow-sm hover:bg-[var(--color-bg-muted)] focus:outline-hidden"
                >
                  {t('workspace.accountMenu.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="cursor-pointer py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-[var(--color-danger)] text-white hover:bg-red-700 focus:outline-hidden"
                >
                  {t('workspace.accountMenu.signOut')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
